import { Component, computed, input, signal, forwardRef, ChangeDetectionStrategy, effect, output, untracked, HostBinding, inject, TemplateRef, viewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { MatLegacyDialog as MatDialog, MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { ServiceStackTypes, AutoscalingProfile, AutoscalingProfileOverridesValue } from '@zerops/models/service-stack';
import { ProfileOverridesFormComponent } from '@zerops/zui/profile-overrides-form';
import { ServiceStackCpuModes, ServiceStackHorizontalAutoscaling, ServiceStackVerticalAutoscaling } from '@zerops/models/settings';
import { ScalingTypes, ScalingDialogData, DialogMetaTabs } from '../autoscaling-form-field/autoscaling-form.model';
import { NouisliderModule } from 'ng2-nouislider';
import { ZuiMaterialFormStackModule } from '@zerops/zui/material-form-stack';
import { FormsModule } from '@angular/forms';
import { ZefFormsModule } from '@zerops/zef/forms';
import { MaterialStackModule } from '@zerops/zerops/core/material-stack';
import { NgTemplateOutlet } from '@angular/common';
import { ZefScrollbarComponent } from '@zerops/zef/scrollbar';
import { ZuiSeparatorComponent } from '@zerops/zui/separator';
import { ZefProgressErrorModule } from '@zerops/zef/progress-error';
import { MatIconModule } from '@angular/material/icon';
import { SCALING_DIALOG_DATA } from './scaling-text.constant';
import {
  createScalingConfiguration,
  extractScalingValues,
  resolveEffectivePlaceholders,
  computeOverLimitFields,
  SCALING_BUTTON_VALUES,
  SCALING_TYPE_SUFFIX_MAP,
  checkLimits,
  isContainerScalingAvailable
} from './autoscaling-settings-form.utils';
import { NgPipesModule } from 'ngx-pipes';
import { MarkdownModule } from 'ngx-markdown';
import { AdjustAutoscalingOutput } from '@zerops/zui/autoscaling-form-field';

@Component({
  selector: 'zui-autoscaling-settings-form',
  templateUrl: 'autoscaling-settings-form.component.html',
  styleUrls: ['autoscaling-settings-form.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NouisliderModule,
    ZuiMaterialFormStackModule,
    FormsModule,
    ZefFormsModule,
    MaterialStackModule,
    NgTemplateOutlet,
    ZefScrollbarComponent,
    ZuiSeparatorComponent,
    ZefProgressErrorModule,
    NgPipesModule,
    MarkdownModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    ProfileOverridesFormComponent
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutoscalingSettingsFormComponent),
      multi: true
    }
  ]
})
export class AutoscalingSettingsFormComponent implements ControlValueAccessor {
  @HostBinding('class.is-embedded') get embeddedClass() { return this.embedded(); }

  // # Deps
  #dialog = inject(MatDialog);
  profileSwitchConfirmTemplateRef = viewChild<TemplateRef<{
    profileName: string;
    droppedKeys: string[];
  }>>('profileSwitchConfirmTemplate');

  // # Inputs

  // Scaling values defined by the user
  value = input<AdjustAutoscalingOutput | null>();
  supportsVerticalAutoscaling = input<boolean>(true);

  // CPU and RAM use fixed mode when vertical autoscaling is not supported
  cpuRamFixedMode = computed(() => !this.supportsVerticalAutoscaling());
  serviceStackType = input<ServiceStackTypes>();
  initialScalingType = input<string>();
  // When true, disables scroll constraints (used when embedded inline, not in a dialog)
  embedded = input<boolean>(false);
  // Limit values 'min' & 'max' for vertical scaling (ram, disc, cpu)
  verticalLimits = input<ServiceStackVerticalAutoscaling>(undefined);
  // Limit value 'min' & 'max' for horizontal scaling (container)
  horizontalLimits = input<ServiceStackHorizontalAutoscaling>(undefined);
  // Default values for vertical scaling (ram, disc, cpu)
  verticalPlaceholder = input<ServiceStackVerticalAutoscaling>(undefined);
  // Default values for horizontal scaling (container)
  horizontalPlaceholder = input<ServiceStackHorizontalAutoscaling>(undefined);
  // Autoscaling profiles from settings API
  autoscalingProfiles = input<AutoscalingProfile[]>([]);

  // Current effective CPU mode of the running service (for edit mode warning)
  currentServiceCpuMode = input<ServiceStackCpuModes>(undefined);
  // Whether this is editing an existing service (shows CPU mode change warning)
  isEditMode = input<boolean>(false);
  // When true, shows the follow-up sentence about reapplying after limits increase.
  // Wire from client.verifiedByTopUp === false (&& verifiedManually === false when available).
  isFreeUser = input<boolean>(false);

  // # Outputs
  valueChange = output<any>();
  validationChange = output<boolean>();

  // # Profile computed
  sortedProfiles = computed(() =>
    [...(this.autoscalingProfiles() ?? [])].sort((a, b) => a.order - b.order)
  );

  // The native <option> title is plain text, so strip the markdown hard-break backslashes
  // (a `\` at end of line) that would otherwise show literally in the tooltip.
  profileTitles = computed<Record<string, string>>(() =>
    this.sortedProfiles().reduce((acc, profile) => {
      acc[profile.id] = (profile.description ?? '').replace(/\\(?=\r?\n|$)/g, '');
      return acc;
    }, {} as Record<string, string>)
  );

  selectedProfileId = computed(() => this.#value()?.autoscalingProfileId);

  // Mirrors what the <select> displays. Diverges from `selectedProfileId` only while a confirm
  // dialog is open: the user's pick is shown immediately, but `#value` only commits on confirm.
  // On cancel we set this back to revert the visible selection without DOM access.
  selectedProfileIdLocal = signal<string | undefined>(undefined);

  selectedProfile = computed(() =>
    this.sortedProfiles().find((p) => p.id === this.selectedProfileId())
  );

  hasProfiles = computed(() => (this.autoscalingProfiles() ?? []).length > 0);

  // # Profile overrides
  selectedProfileOverridesSchema = computed(() => this.selectedProfile()?.overrides ?? {});
  hasOverridesSchema = computed(() => Object.keys(this.selectedProfileOverridesSchema()).length > 0);
  overridesSchemaSize = computed(() => Object.keys(this.selectedProfileOverridesSchema()).length);
  currentOverrides = computed<AutoscalingProfileOverridesValue>(
    () => this.#value()?.autoscalingProfileOverrides ?? {}
  );
  overridesSetCount = computed(() => Object.keys(this.currentOverrides()).length);

  // # Tab state
  isOverridesTabActive = computed(() => this.scalingData()?.type === DialogMetaTabs.Overrides);

  selectOverridesTab() {
    this.scalingData.update((data) => ({ ...data, type: DialogMetaTabs.Overrides }));
  }

  // # CPU mode computeds
  // The explicit cpuMode from the form value (null = Default)
  explicitCpuMode = computed(() => this.#value()?.verticalAutoscaling?.cpuMode as ServiceStackCpuModes | null);

  // The resolved default cpuMode (from profile or base config)
  resolvedDefaultCpuMode = computed(() =>
    this.effectiveVerticalPlaceholder()?.cpuMode ?? this.verticalLimits()?.cpuMode ?? 'SHARED' as ServiceStackCpuModes
  );

  // The effective cpuMode that will be applied
  effectiveCpuMode = computed(() => this.explicitCpuMode() || this.resolvedDefaultCpuMode());

  // Whether the effective CPU mode differs from the current running service's mode
  cpuModeWillChange = computed(() => {
    const current = this.currentServiceCpuMode();
    if (!current || !this.isEditMode()) return false;
    return this.effectiveCpuMode() !== current;
  });

  cpuModeChangeDescription = computed(() => {
    const current = this.currentServiceCpuMode();
    const next = this.effectiveCpuMode();
    if (!current) return '';
    return `CPU mode will change from ${current === 'SHARED' ? 'Shared' : 'Dedicated'} to ${next === 'SHARED' ? 'Shared' : 'Dedicated'}`;
  });

  // Show HA suggestion when container scaling exists and effective min container count is 1
  showHaSuggestion = computed(() => {
    if (!this.hasContainerScaling()) return false;
    const currentMin = this.#value()?.horizontalAutoscaling?.minContainerCount;
    const placeholderMin = this.effectiveHorizontalPlaceholder()?.minContainerCount;
    return (currentMin ?? placeholderMin ?? 1) <= 1;
  });

  // Effective placeholders shown in the form (and as HTML input placeholders via scalingData):
  //  - no profile selected → defaultAutoscaling (verticalPlaceholder / horizontalPlaceholder)
  //  - any profile selected → config base (verticalLimits / horizontalLimits) with profile's customAutoscaling overlaid
  #effectivePlaceholders = computed(() => resolveEffectivePlaceholders(
    { vertical: this.verticalPlaceholder(), horizontal: this.horizontalPlaceholder() },
    { vertical: this.verticalLimits(), horizontal: this.horizontalLimits() },
    this.selectedProfile()
  ));

  effectiveVerticalPlaceholder = computed(() => this.#effectivePlaceholders().vertical);

  effectiveHorizontalPlaceholder = computed(() => this.#effectivePlaceholders().horizontal);

  // Fields whose effective value sits outside the client's current limits.
  // Drives the tab badges and the footer warning banner.
  overLimitFields = computed(() => computeOverLimitFields({
    value: this.#value(),
    verticalPlaceholder: this.effectiveVerticalPlaceholder(),
    horizontalPlaceholder: this.effectiveHorizontalPlaceholder(),
    verticalLimits: this.verticalLimits(),
    horizontalLimits: this.horizontalLimits(),
    cpuRamFixedMode: this.cpuRamFixedMode()
  }));

  hasOverLimitFields = computed(() => this.overLimitFields().length > 0);

  overLimitByTab = computed(() => {
    const byTab = {} as Record<ScalingTypes, boolean>;
    this.overLimitFields().forEach((f) => { byTab[f.scalingType] = true; });
    return byTab;
  });

  overLimitDetailsOpen = signal(false);

  toggleOverLimitDetails() {
    this.overLimitDetailsOpen.update((open) => !open);
  }

  // # Control Value Accessor
  #value = signal<any>({});
  #propagateChange = (_: any) => { /* ControlValueAccessor callback */ };
  #propagateTouched = () => { /* ControlValueAccessor callback */ };

  // # Data for template compatibility
  scalingTypes = ScalingTypes;
  scalingData = signal<ScalingDialogData>({
    type: undefined,
    [ScalingTypes.Container]: undefined,
    [ScalingTypes.Cpu]: undefined,
    [ScalingTypes.Ram]: undefined,
    [ScalingTypes.Disc]: undefined
  });

  // # Memoized slider limits - computed once, never change during component lifecycle
  /*
  #sliderLimits = computed(() => {
    const defaultHorizontal = this.defaultHorizontalAutoscaling();
    const defaultVertical = this.defaultVerticalAutoscaling();

    if (!defaultVertical) return null;

    return {
      [ScalingTypes.Container]: getScalingSliderLimits(ScalingTypes.Container, defaultHorizontal, defaultVertical),
      [ScalingTypes.Cpu]: getScalingSliderLimits(ScalingTypes.Cpu, defaultHorizontal, defaultVertical),
      [ScalingTypes.Ram]: getScalingSliderLimits(ScalingTypes.Ram, defaultHorizontal, defaultVertical),
      [ScalingTypes.Disc]: getScalingSliderLimits(ScalingTypes.Disc, defaultHorizontal, defaultVertical)
    };
  });
   */

  // Validation computed - replaces validation effect
  #isValid = computed(() => {
    const scalingData = this.scalingData();
    const cpuRamFixedMode = this.cpuRamFixedMode();
    const verticalLimits = this.verticalLimits();
    const horizontalLimits = this.horizontalLimits();
    const verticalPlaceholder = this.effectiveVerticalPlaceholder();
    const horizontalPlaceholder = this.effectiveHorizontalPlaceholder();

    const _data = {
      [ScalingTypes.Cpu]: {
        min: scalingData[ScalingTypes.Cpu]?.min || verticalPlaceholder?.minResource.cpuCoreCount,
        max: scalingData[ScalingTypes.Cpu]?.max || verticalPlaceholder?.maxResource.cpuCoreCount,
        minLimit: verticalLimits?.minResource.cpuCoreCount,
        maxLimit: verticalLimits?.maxResource.cpuCoreCount
      },
      [ScalingTypes.Ram]: {
        min: scalingData[ScalingTypes.Ram]?.min || verticalPlaceholder?.minResource.memoryGBytes,
        max: scalingData[ScalingTypes.Ram]?.max || verticalPlaceholder?.maxResource.memoryGBytes,
        minLimit: verticalLimits?.minResource.memoryGBytes,
        maxLimit: verticalLimits?.maxResource.memoryGBytes
      },
      [ScalingTypes.Disc]: {
        min: scalingData[ScalingTypes.Disc]?.min || verticalPlaceholder?.minResource.diskGBytes,
        max: scalingData[ScalingTypes.Disc]?.max || verticalPlaceholder?.maxResource.diskGBytes,
        minLimit: verticalLimits?.minResource.diskGBytes,
        maxLimit: verticalLimits?.maxResource.diskGBytes
      },
      [ScalingTypes.Container]: {
        min: scalingData[ScalingTypes.Container]?.min || horizontalPlaceholder?.minContainerCount,
        max: scalingData[ScalingTypes.Container]?.max || horizontalPlaceholder?.maxContainerCount,
        minLimit: horizontalLimits?.minContainerCount,
        maxLimit: horizontalLimits?.maxContainerCount
      }
    }

    // CPU and RAM use fixed mode when vertical autoscaling is not supported; Disk always uses range mode
    const isCpuInvalid = checkLimits(_data[ScalingTypes.Cpu], cpuRamFixedMode);
    const isRamInvalid = checkLimits(_data[ScalingTypes.Ram], cpuRamFixedMode);
    const isDiscInvalid = checkLimits(_data[ScalingTypes.Disc], false);
    // Container is not a single-mode; container availability follows the same rule as the tab/form
    const isContainerInvalid = isContainerScalingAvailable(horizontalLimits) && checkLimits(_data[ScalingTypes.Container], false);

    return !(isCpuInvalid || isRamInvalid || isDiscInvalid || isContainerInvalid);
  });

  hasContainerScaling = computed(() => isContainerScalingAvailable(this.horizontalLimits()));

  scalingDataType = computed(() => this.scalingData()?.type);

  // MatInput resolves its placeholder via `placeholder || null`, so a numeric 0 (a valid default,
  // e.g. minContainerCount: 0) would be dropped and render no placeholder at all. Coerce to a
  // string so '0' survives the truthiness check; nullish stays '' (MatInput renders nothing, as before).
  placeholderOf(value: number | null | undefined): string {
    return value == null ? '' : `${value}`;
  }

  // Treats 0 as a real value (unlike a plain truthy check), so a min/limit of 0 still renders its
  // label and range (e.g. container scaling "0 ↔ 10"); null/undefined are treated as absent.
  isNumeric(value: number | null | undefined): boolean {
    return typeof value === 'number';
  }

  // Computed properties for button states - much more efficient than methods!
  buttonStates = computed(() => {
    const scaling = this.scalingData();
    const minToggle = this.minToggleButtonValue();
    const maxToggle = this.maxToggleButtonValue();

    const states: Record<ScalingTypes, {
      canDecrementMin: (minLimit?: number) => boolean;
      canIncrementMin: (maxLimit?: number) => boolean;
      canDecrementMax: (minLimit?: number) => boolean;
      canIncrementMax: (maxLimit?: number) => boolean;
    }> = {} as any;

    Object.values(ScalingTypes).forEach(scalingType => {
      const data = scaling[scalingType];
      const minCurrent = data?.min || 0;
      const maxCurrent = data?.max || 0;
      const defaultMin = data?.defaultMin || 0;
      const defaultMax = data?.defaultMax || 0;
      const minDecrement = minToggle[scalingType] || 0;
      const maxDecrement = maxToggle[scalingType] || 0;

      states[scalingType] = {
        canDecrementMin: (minLimit?: number) => minLimit !== undefined
          ? (minCurrent && (minCurrent - minDecrement) >= minLimit) || (!minCurrent && (defaultMin - minDecrement) >= minLimit)
          : true,
        canIncrementMin: (maxLimit?: number) => maxLimit !== undefined
          ? (minCurrent && (minCurrent + minDecrement) <= maxLimit) || (!minCurrent && (defaultMin + minDecrement) <= maxLimit)
          : true,
        canDecrementMax: (minLimit?: number) => minLimit !== undefined
          ? (maxCurrent && (maxCurrent - maxDecrement) >= minLimit) || (!maxCurrent && (defaultMax - maxDecrement) >= minLimit)
          : true,
        canIncrementMax: (maxLimit?: number) => maxLimit !== undefined
          ? (maxCurrent && (maxCurrent + maxDecrement) <= maxLimit) || (!maxCurrent && (defaultMax + maxDecrement) <= maxLimit)
          : true,
      };
    });

    return states;
  });

  scalingDataComputed = computed(() => {
    const serviceStackType = this.serviceStackType();
    const verticalLimitValue = this.verticalLimits();
    const currentValue = this.#value();
    const isEmbedded = this.embedded();
    const horizontalLimits = this.horizontalLimits();
    const defaultTab = isEmbedded ? undefined : (isContainerScalingAvailable(horizontalLimits) ? ScalingTypes.Container : ScalingTypes.Cpu);
    const tab = (this.initialScalingType() as ScalingTypes) || defaultTab;

    if (serviceStackType && verticalLimitValue) {
      // Set real values for scaling
      const values = extractScalingValues(currentValue);
      const currentTab = untracked(() => this.scalingData()?.type) ?? tab;
      return createScalingConfiguration(currentTab, values, this.effectiveVerticalPlaceholder(), this.effectiveHorizontalPlaceholder());
    }

    return this.scalingData(); // Return current value if conditions not met
  });

  // # Public API for template
  get internalValue() {
    return this.#value();
  }

  // # ControlValueAccessor Implementation
  writeValue(value: any): void {
    this.#value.set(value || {});
  }

  registerOnChange(fn: any): void {
    this.#propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.#propagateTouched = fn;
  }

  setDisabledState?(_isDisabled: boolean): void {
    // Handle disabled state if needed
  }

  // # Template Helper Methods
  changeTab(tab: ScalingTypes) {
    // In embedded mode, clicking the active tab collapses it
    if (this.embedded() && this.scalingData()?.type === tab) {
      this.scalingData.update(data => ({ ...data, type: undefined }));
    } else {
      this.scalingData.update(data => ({ ...data, type: tab }));
    }
  }

  setHighAvailability() {
    const currentValue = JSON.parse(JSON.stringify(this.#value() || {}));
    if (!currentValue.horizontalAutoscaling) currentValue.horizontalAutoscaling = {};
    currentValue.horizontalAutoscaling.minContainerCount = 2;
    this.#commitValueChange(currentValue);
  }

  async selectProfile(profileId: string) {
    const currentValue = JSON.parse(JSON.stringify(this.#value() || {}));
    const previousProfileId = currentValue.autoscalingProfileId;

    if (previousProfileId === profileId) return;

    // Reflect the user's pick in the bound <select> immediately; if they cancel below we
    // revert this signal and Angular re-renders the select to the previous value.
    this.selectedProfileIdLocal.set(profileId);

    const newProfile = this.sortedProfiles().find((p) => p.id === profileId);
    const newSchema = newProfile?.overrides ?? {};
    const currentOverrides: AutoscalingProfileOverridesValue = currentValue.autoscalingProfileOverrides ?? {};
    const droppedKeys = Object.keys(currentOverrides).filter((k) => !(k in newSchema));

    if (droppedKeys.length === 0) {
      currentValue.autoscalingProfileId = profileId;
      this.#commitValueChange(currentValue);
      return;
    }

    const tpl = this.profileSwitchConfirmTemplateRef();
    if (!tpl) return;

    const confirmed = await firstValueFrom(this.#dialog.open(tpl, {
      data: { profileName: newProfile?.name ?? profileId, droppedKeys },
      width: '420px',
      autoFocus: false
    }).afterClosed());

    if (!confirmed) {
      this.selectedProfileIdLocal.set(previousProfileId);
      return;
    }

    const next: AutoscalingProfileOverridesValue = {};
    Object.entries(currentOverrides).forEach(([ k, v ]) => { if (k in newSchema) next[k] = v; });
    const applied = JSON.parse(JSON.stringify(this.#value() || {}));
    applied.autoscalingProfileId = profileId;
    applied.autoscalingProfileOverrides = next;
    this.#commitValueChange(applied);
  }

  onOverridesChange(next: AutoscalingProfileOverridesValue) {
    const currentValue = JSON.parse(JSON.stringify(this.#value() || {}));
    // Always store as an object (even when empty) so ngrx-forms' lazy control creation
    // infers a FormGroup, not a primitive FormControl. A primitive control would later
    // choke on object values like {jit: false} when the user adds the first override.
    currentValue.autoscalingProfileOverrides = next ?? {};
    this.#commitValueChange(currentValue);
  }

  setCpuMode(mode: ServiceStackCpuModes | null) {
    const currentValue = JSON.parse(JSON.stringify(this.#value() || {}));
    if (!currentValue.verticalAutoscaling) currentValue.verticalAutoscaling = {};
    currentValue.verticalAutoscaling.cpuMode = mode;
    this.#commitValueChange(currentValue);
  }

  // Helper methods for button increase calculations
  incrementValue(scalingType: ScalingTypes, property: 'min' | 'max'): void {
    const data = this.scalingData()[scalingType];
    const defaultMin = data?.defaultMin || 0;
    const defaultMax = data?.defaultMax || 0;
    const currentValue = data?.[property] || 0;
    const increment = property === 'min' ? this.minToggleButtonValue()[scalingType] : this.maxToggleButtonValue()[scalingType];
    let newValue = undefined;
    if (currentValue) {
      newValue = currentValue + increment;
    } else {
      if (property === 'min') {
        newValue = defaultMin + increment;
      } else {
        newValue = defaultMax + increment;
      }
    }
    // Fixed mode applies to CPU/RAM only (not Container or Disk)
    const isFixedMode = this.cpuRamFixedMode() && (scalingType === ScalingTypes.Cpu || scalingType === ScalingTypes.Ram);
    const scalingProperty = isFixedMode ? 'singleModel' : property;
    this.updateScalingProperty(scalingType, scalingProperty, newValue);
  }

  // Helper methods for button decrease calculations
  decrementValue(scalingType: ScalingTypes, property: 'min' | 'max'): void {
    const data = this.scalingData()[scalingType];
    const defaultMin = data?.defaultMin || 0;
    const defaultMax = data?.defaultMax || 0;
    const currentValue = data?.[property] || 0;
    const decrement = property === 'min' ? this.minToggleButtonValue()[scalingType] : this.maxToggleButtonValue()[scalingType];
    let newValue = undefined;
    if (currentValue) {
      newValue = currentValue - decrement;
    } else {
      if (property === 'min') {
        newValue = defaultMin - decrement;
      } else {
        newValue = defaultMax - decrement;
      }
    }
    // Fixed mode applies to CPU/RAM only (not Container or Disk)
    const isFixedMode = this.cpuRamFixedMode() && (scalingType === ScalingTypes.Cpu || scalingType === ScalingTypes.Ram);
    const scalingProperty = isFixedMode ? 'singleModel' : property;
    this.updateScalingProperty(scalingType, scalingProperty, newValue);
  }

  changeMaxButtonStep(scalingType: ScalingTypes, value: number) {
    this.maxToggleButtonValue.update(data => ({ ...data, [scalingType]: value }));
  }

  changeMinButtonStep(scalingType: ScalingTypes, value: number) {
    this.minToggleButtonValue.update(data => ({ ...data, [scalingType]: value }));
  }

  updateScalingProperty<T>(scalingType: ScalingTypes, property: string, newValue: T) {
    // Create a deep copy to avoid read-only property issues
    const currentValue = this.#value();
    const updatedValue = JSON.parse(JSON.stringify(currentValue || {}));

    // Convert scaling UI changes to proper form structure
    this.#updateScalingTypeProperty(updatedValue, scalingType, property, newValue);

    // Update the internal form state and emit the change
    this.#commitValueChange(updatedValue);
  }

  #updateScalingTypeProperty<T>(updatedValue: any, scalingType: ScalingTypes, property: string, newValue: T) {
    switch (scalingType) {
      case ScalingTypes.Container:
        this.#updateContainerScaling(updatedValue, property, newValue);
        break;
      case ScalingTypes.Cpu:
        this.#updateCpuScaling(updatedValue, property, newValue);
        break;
      case ScalingTypes.Ram:
        this.#updateRamScaling(updatedValue, property, newValue);
        break;
      case ScalingTypes.Disc:
        this.#updateDiscScaling(updatedValue, property, newValue);
        break;
    }
  }

  #updateContainerScaling<T>(updatedValue: any, property: string, newValue: T) {
    if (!updatedValue.horizontalAutoscaling) updatedValue.horizontalAutoscaling = {};

    if (property === 'min') {
      updatedValue.horizontalAutoscaling.minContainerCount = newValue;
    } else if (property === 'max') {
      updatedValue.horizontalAutoscaling.maxContainerCount = newValue;
    } else if (property === 'reset') {
      updatedValue.horizontalAutoscaling.minContainerCount = newValue;
      updatedValue.horizontalAutoscaling.maxContainerCount = newValue;
    }
  }

  #updateCpuScaling<T>(updatedValue: any, property: string, newValue: T) {
    this.#ensureVerticalAutoscalingStructure(updatedValue);

    if (property === 'min') {
      updatedValue.verticalAutoscaling.minResource.cpuCoreCount = newValue;
    } else if (property === 'max') {
      updatedValue.verticalAutoscaling.maxResource.cpuCoreCount = newValue;
    } else if (property === 'reset') {
      updatedValue.verticalAutoscaling.minResource.cpuCoreCount = newValue;
      updatedValue.verticalAutoscaling.maxResource.cpuCoreCount = newValue;
    } else if (property === 'singleModel') {
      updatedValue.verticalAutoscaling.minResource.cpuCoreCount = newValue;
      updatedValue.verticalAutoscaling.maxResource.cpuCoreCount = newValue;
      updatedValue.verticalAutoscaling.startCpuCoreCount = null;
    } else if (property === 'startCount') {
      updatedValue.verticalAutoscaling.startCpuCoreCount = this.supportsVerticalAutoscaling()
        ? newValue
        : null;
    } else if (property === 'minFreeNum') {
      // min-free-resource may be 'null'
      updatedValue.verticalAutoscaling.minFreeResource = {
        ...updatedValue.verticalAutoscaling.minFreeResource,
        cpuCoreCount: newValue
      };
    } else if (property === 'minFreePct') {
      // min-free-resource may be 'null'
      updatedValue.verticalAutoscaling.minFreeResource = {
        ...updatedValue.verticalAutoscaling.minFreeResource,
        cpuCorePercent: newValue
      };
    }
  }

  #updateRamScaling<T>(updatedValue: any, property: string, newValue: T) {
    this.#ensureVerticalAutoscalingStructure(updatedValue);

    if (property === 'min') {
      updatedValue.verticalAutoscaling.minResource.memoryGBytes = newValue;
    } else if (property === 'max') {
      updatedValue.verticalAutoscaling.maxResource.memoryGBytes = newValue;
    } else if (property === 'singleModel' || property === 'reset') {
      updatedValue.verticalAutoscaling.minResource.memoryGBytes = newValue;
      updatedValue.verticalAutoscaling.maxResource.memoryGBytes = newValue;
    } else if (property === 'minFreePct') {
      // min-free-resource may be 'null'
      updatedValue.verticalAutoscaling.minFreeResource = {
        ...updatedValue.verticalAutoscaling.minFreeResource,
        memoryPercent: newValue
      };
    } else if (property === 'minFree') {
      // min-free-resource may be 'null'
      updatedValue.verticalAutoscaling.minFreeResource = {
        ...updatedValue.verticalAutoscaling.minFreeResource,
        memoryGBytes: newValue
      };
    }
  }

  #updateDiscScaling<T>(updatedValue: any, property: string, newValue: T) {
    this.#ensureVerticalAutoscalingStructure(updatedValue);

    if (property === 'min') {
      updatedValue.verticalAutoscaling.minResource.diskGBytes = newValue;
    } else if (property === 'max') {
      updatedValue.verticalAutoscaling.maxResource.diskGBytes = newValue;
    } else if (property === 'singleModel' || property === 'reset') {
      updatedValue.verticalAutoscaling.minResource.diskGBytes = newValue;
      updatedValue.verticalAutoscaling.maxResource.diskGBytes = newValue;
    }
  }

  #ensureVerticalAutoscalingStructure(updatedValue: any) {
    if (!updatedValue.verticalAutoscaling) updatedValue.verticalAutoscaling = {};
    if (!updatedValue.verticalAutoscaling.minResource) updatedValue.verticalAutoscaling.minResource = {};
    if (!updatedValue.verticalAutoscaling.maxResource) updatedValue.verticalAutoscaling.maxResource = {};
  }

  #commitValueChange(updatedValue: any) {
    this.#value.set(updatedValue);
    this.#propagateChange(updatedValue);
    this.#propagateTouched();
    this.valueChange.emit(updatedValue);
  }

  // # Template data
  templateContent = SCALING_DIALOG_DATA;
  scalingTypeSuffixMap = SCALING_TYPE_SUFFIX_MAP;
  scalingButtonValues = SCALING_BUTTON_VALUES;

  // # Template helpers
  valueRef() {
    return this.#value();
  }

  // # Missing template functions
  minToggleButtonValue = signal<Record<ScalingTypes, number>>({
    [ScalingTypes.Container]: SCALING_BUTTON_VALUES[ScalingTypes.Container]?.buttons?.[0] || 1,
    [ScalingTypes.Cpu]: SCALING_BUTTON_VALUES[ScalingTypes.Cpu]?.buttons?.[0] || 1,
    [ScalingTypes.Ram]: SCALING_BUTTON_VALUES[ScalingTypes.Ram]?.buttons?.[0] || 0.125,
    [ScalingTypes.Disc]: SCALING_BUTTON_VALUES[ScalingTypes.Disc]?.buttons?.[0] || 0.5
  });

  maxToggleButtonValue = signal<Record<ScalingTypes, number>>({
    [ScalingTypes.Container]: SCALING_BUTTON_VALUES[ScalingTypes.Container]?.buttons?.[0] || 1,
    [ScalingTypes.Cpu]: SCALING_BUTTON_VALUES[ScalingTypes.Cpu]?.buttons?.[0] || 1,
    [ScalingTypes.Ram]: SCALING_BUTTON_VALUES[ScalingTypes.Ram]?.buttons?.[0] || 0.125,
    [ScalingTypes.Disc]: SCALING_BUTTON_VALUES[ScalingTypes.Disc]?.buttons?.[0] || 0.5
  });

  constructor() {
    // Sync input value to internal state
    effect(() => {
      const inputValue = this.value();
      const currentInternalValue = untracked(() => this.#value());

      if (JSON.stringify(currentInternalValue) !== JSON.stringify(inputValue)) {
        this.#value.set(inputValue || {});
      }
    }, { allowSignalWrites: true });

    // Syncs computed scaling configuration with internal form state whenever scaling data changes
    effect(() => {
      const computedData = this.scalingDataComputed();
      this.scalingData.set(computedData);
    }, { allowSignalWrites: true });

    // Open selected tab after dialog open
    effect(() => {
      if (this.initialScalingType()) {
        this.changeTab(this.initialScalingType() as ScalingTypes);
      }
    }, { allowSignalWrites: true });

    // Validation effect
    effect(() => {
      this.validationChange.emit(this.#isValid());
    });

    // Default profile pre-selection
    effect(() => {
      const profiles = this.autoscalingProfiles() ?? [];
      const currentProfileId = this.#value()?.autoscalingProfileId;
      if (profiles.length > 0 && !currentProfileId) {
        const defaultProfile = profiles.find((p) => p.isDefault);
        if (defaultProfile) {
          untracked(() => this.selectProfile(defaultProfile.id));
        }
      }
    }, { allowSignalWrites: true });

    // Mirror the committed profile id into the local select-bound signal whenever the form
    // value changes externally (writeValue, default-profile pre-selection, confirm commit).
    effect(() => {
      const committed = this.selectedProfileId();
      untracked(() => {
        if (this.selectedProfileIdLocal() !== committed) {
          this.selectedProfileIdLocal.set(committed);
        }
      });
    }, { allowSignalWrites: true });

    // If the active profile no longer exposes overrides, leave the Overrides tab.
    effect(() => {
      if (this.isOverridesTabActive() && !this.hasOverridesSchema()) {
        untracked(() => this.scalingData.update((data) => ({ ...data, type: undefined })));
      }
    }, { allowSignalWrites: true });
  }
}
